import { AppRequestError } from "@pilotfish/app-api";
import { type FormEvent, useId, useState } from "react";
import { useNavigate } from "react-router-dom";
import { startFamily } from "../api/memberships";
import { Card } from "../components/Card";
import { standing } from "../components/FamilySwitcher";
import { Screen } from "../components/Screen";
import { usePanel } from "../panelContext";
import { HOUSEHOLDS_SECTION } from "../sections";
import { spell } from "../words";

/**
 * `/me/households` — **the households somebody is in, and the way to start another**
 * (issue #360).
 *
 * ## Why the page is here and not under a family
 *
 * How many households you are in, and starting one, are facts about a **person** (ADR 0021
 * decision 2). There is no family to scope either by, and ADR 0022 decision 2a puts the
 * twenty-household bound on the person too. It is also where somebody with *one* household
 * starts a second: the switcher is a label rather than a menu at one household, on purpose, so
 * the affordance needs a home that exists whatever the count.
 *
 * ## Every word of a refusal is the server's
 *
 * The three a person can actually meet — a name they have already used, the twentieth
 * household, and the posture flag being off — each arrive as a `message` and are rendered
 * unedited. That is #158's rule, and it earns its place twice here: the duplicate-name refusal
 * *names the household that already holds the name*, which no sentence this bundle could
 * compose would know, and the ceiling refusal is the one piece of copy in the product most
 * likely to be rewritten into an upsell (ADR 0022 decision 3 — nothing raises that number).
 *
 * ## What it does afterwards
 *
 * It reloads the shell's memberships and navigates into the new household, so *"and the panel
 * afterwards shows both and is working in the new one"* is one action rather than a hint. A
 * **resubmit lands in exactly the same place** — the server answers with the household the
 * first attempt made, so a double-click ends up where the first click was going.
 *
 * ## Who reaches it
 *
 * **Somebody with at least one household, always** — the shell shows `NoFamily` instead of the
 * scopes when there are none (`Panel.tsx`), so this page cannot render an empty list and its
 * copy does not hedge about one. That is not an oversight about the person with no household:
 * their route in is the server's own sentence, per #360's reading of *"or is sent somewhere
 * that works"*. The **route** serves them (`POST /app/me/families` takes a caller with no
 * memberships); this page is simply not where they meet it.
 *
 * ## What it does not do
 *
 * **No renaming, and no leaving.** Renaming is #345; leaving is `leave_family`, which lives on
 * the family's own roster where the consequence is. A page that listed households would invite
 * both, and both belong where they already are.
 */
export function Households() {
  const { families } = usePanel();

  return (
    <Screen title={HOUSEHOLDS_SECTION.label} blurb={HOUSEHOLDS_SECTION.blurb}>
      <Card>
        <h2 className="households__heading">
          {families.length === 1 ? "Your household" : `Your ${spell(families.length)} households`}
        </h2>
        <ul className="households__list">
          {families.map((family) => (
            <li className="households__item" key={family.familyId}>
              <span className="avatar avatar--square" aria-hidden="true" />
              <span className="households__names">
                <span className="households__name">{family.name ?? "Your family"}</span>
                {standing(family) === null ? null : (
                  <span className="households__note">{standing(family)}</span>
                )}
              </span>
            </li>
          ))}
        </ul>
      </Card>
      <StartHousehold />
    </Screen>
  );
}

/**
 * The form.
 *
 * **Two fields, and only one of them required.** The household's name is what the refusals are
 * about and is the only thing the server insists on; the creator's own name is cosmetic and
 * falls back to the account's, so an empty one still starts a household rather than failing
 * one. That is `createFamilyRoutes.ts`'s posture, matched here so the two browser surfaces do
 * not disagree about what a create needs.
 *
 * **It validates almost nothing, deliberately** — `InviteForm`'s rule for its reason: the
 * server's `cleanName` is the bound, and a browser-side rule stricter than the server's would
 * refuse a household name the product would happily have accepted. The `maxLength` matches
 * `FAMILY_NAME_MAX_LENGTH` so nobody types 200 characters and is silently given 80.
 */
function StartHousehold() {
  const { api, families, reload } = usePanel();
  const navigate = useNavigate();
  const nameId = useId();
  const yourNameId = useId();
  /**
   * The refusal's own id, so the name field can point at it (PR #361 review).
   *
   * **Unconditional, where `aria-invalid` is not.** Whatever went wrong, the alert is the
   * account of it, and a field that describes itself with the page's one error message is
   * better than a screen-reader user hearing a sentence at the foot of the form and working
   * out which of two inputs it concerns. Whether the *name* is the thing to change is a
   * narrower question, and {@link Refusal} answers it separately.
   */
  const refusalId = useId();
  const [name, setName] = useState("");
  const [yourName, setYourName] = useState("");
  const [pending, setPending] = useState(false);
  const [refusal, setRefusal] = useState<Refusal | null>(null);

  const trimmed = name.trim();
  const ready = trimmed.length > 0;

  function submit(event: FormEvent): void {
    event.preventDefault();
    if (!ready || pending) return;
    setPending(true);
    setRefusal(null);

    const creator = yourName.trim();
    startFamily(api, { name: trimmed, ...(creator.length > 0 ? { displayName: creator } : {}) })
      .then((started) => {
        // Re-read the memberships *before* navigating, so the switcher already knows about the
        // household the next screen is scoped to. Without it the family scope renders against
        // a list that has not caught up and reads as "not one of yours".
        reload();
        navigate(`/families/${started.familyId}`);
      })
      .catch((error: unknown) => {
        setPending(false);
        setRefusal(describeRefusal(error));
      });
  }

  return (
    <Card>
      <form className="inviteform" onSubmit={submit}>
        <h2 className="inviteform__title">Start another household</h2>

        <div className="field">
          <label className="field__label" htmlFor={nameId}>
            What to call it
          </label>
          <input
            className="field__input"
            id={nameId}
            type="text"
            value={name}
            onChange={(event) => {
              setName(event.target.value);
              // The judgement was about the string that was submitted, so editing it retires
              // the refusals editing can undo — see {@link Refusal} on the lifetime, and on
              // why the others deliberately survive a keystroke.
              if (refusal?.nameFixable === true) setRefusal(null);
            }}
            disabled={pending}
            placeholder="Mum's place"
            maxLength={80}
            autoComplete="off"
            /* Only when a different name would actually help — see {@link Refusal}. The
               description stays whatever happened, because the alert is the account of it. */
            aria-invalid={refusal?.nameFixable === true}
            aria-describedby={refusal === null ? undefined : refusalId}
          />
          {/*
            The rule #360 adds, said before somebody meets it rather than after. It is a
            sentence about *their* households and not about the product: two unrelated families
            are both entitled to be called "Home".
          */}
          <p className="field__hint">
            Give it a name you'll recognise beside {theOthers(families.length)} — Boone won't let
            you have two of your own with the same name, because neither of us could tell them apart
            afterwards.
          </p>
        </div>

        <div className="field">
          <label className="field__label" htmlFor={yourNameId}>
            Your name in it <span className="field__optional">(optional)</span>
          </label>
          <input
            className="field__input"
            id={yourNameId}
            type="text"
            value={yourName}
            onChange={(event) => setYourName(event.target.value)}
            disabled={pending}
            placeholder="What this household calls you"
            maxLength={80}
            autoComplete="off"
          />
        </div>

        <div className="inviteform__cost">
          <p>
            You'll be its owner. It's separate from everything else Boone keeps for you — its own
            list, its own memory, its own people — and nothing crosses between them.
          </p>
          {/*
            **Said before the button, because it is the one effect that reaches outside this
            tab** (PR #361 review). `commitFamilyCreation` seeds `ACTIVE_FAMILY` on every
            create — ADR 0023 decision 4's *"the flow that creates the ambiguity should resolve
            it"* — so this moves what @boone works in, where switching household in the header
            deliberately does not.

            Unconditional, because everyone reading it already has a household to be moved
            away from: see {@link Households} on why this page is unreachable without one.
          */}
          <p>
            @boone will start working in it too, next time you talk. Ask it to switch back whenever
            you want.
          </p>
        </div>

        {refusal === null ? null : (
          <p className="inviteform__error" id={refusalId} role="alert">
            {refusal.message}
          </p>
        )}

        <div className="inviteform__actions">
          <button className="cta" type="submit" disabled={!ready || pending}>
            {pending ? "Starting…" : "Start it"}
          </button>
        </div>
      </form>
    </Card>
  );
}

/** How to refer to the households they already have — never none; see {@link Households}. */
function theOthers(count: number): string {
  return count === 1 ? "the one you already have" : "the ones you already have";
}

/**
 * What to show when a create did not happen — **and whether the field is what to change**.
 *
 * The two travel together rather than as two `useState`s, because they are one answer and the
 * bug they replace is what happens when they drift: `aria-invalid` was `refusal !== null`, so
 * every refusal marked the name field invalid (PR #361 review). Most of them are not about the
 * name at all.
 */
interface Refusal {
  readonly message: string;
  /**
   * True only when typing a different household name is what fixes this.
   *
   * **It decides two things, and they turn out to be one question** (PR #361 review, round
   * three): whether the field is what to blame, and whether editing the field makes this
   * answer stale. Both reduce to *would a different name change the outcome?*
   *
   * **Two codes, and the rest are deliberately not among them.** The 403s — the posture flag,
   * an unconfirmed address, the twentieth household, a race, an identity lookup that failed —
   * are exactly the set `me.ts` describes as *"none of these is fixed by sending different
   * arguments"*, so telling a screen-reader user their name is invalid points them at the one
   * thing they can change and the one thing that will not help.
   *
   * The clearest case is the fallback, which is not a refusal at all: a request that never
   * reached the server marked the field invalid while {@link describeRefusal}'s own sentence
   * carefully refused to blame the name. The wording was honest and the ARIA state made the
   * claim anyway.
   *
   * `CreateFamily.tsx` draws the same line on the login host — `nameError` for an empty name
   * or a duplicate, everything else rendered without touching the field.
   */
  readonly nameFixable: boolean;
}

/**
 * **How long a refusal stays on screen** — decided rather than inherited (PR #361 review).
 *
 * A name-fixable refusal is retired **on the next keystroke in the name field**, matching
 * `CreateFamily.tsx`. `aria-invalid` is a claim about the value in the input, so the moment
 * that value changes the claim is about a string nothing has judged — and the alert beside it
 * would go on naming *"Home"* while the field reads *Mum's place*. Holding both until the next
 * submit would leave them stale for the whole of the correction, which is the part somebody is
 * actually doing.
 *
 * **Every other refusal survives the keystroke, and that is where this parts company with the
 * sibling.** The login host clears unconditionally because its non-name refusals leave the form
 * altogether — a terminal card, with no field to type in. This page keeps the form up for all
 * of them, so an unconditional clear would drop a still-true sentence (*"you're already in 20
 * households"*) the moment somebody touched the name, and typing does not make it any less
 * true. So one predicate answers both questions: a refusal is retired by an edit exactly when
 * an edit could have fixed it.
 */

/** The codes a different household name would resolve. Everything else is about something else. */
const NAME_FIXABLE_CODES: ReadonlySet<string> = new Set([
  "duplicate_family_name",
  "invalid_family_name",
]);

/**
 * The server's own sentence for a refusal, or a generic line for something that was not one.
 *
 * **Every refusal this route can produce carries a `message`**, and it is rendered unedited —
 * see the module header. The fallback is for the two things that are not answers: a request
 * that never reached the server, and a fault at our end. Neither has a family-facing sentence
 * to relay, and inventing one that *sounded* like a refusal would tell somebody their perfectly
 * good household name had been rejected.
 */
function describeRefusal(error: unknown): Refusal {
  if (error instanceof AppRequestError && error.detail !== null) {
    return { message: error.detail, nameFixable: NAME_FIXABLE_CODES.has(error.code) };
  }
  return {
    message:
      "Something went wrong starting that household. Try again in a moment — nothing else Boone keeps for you is affected.",
    nameFixable: false,
  };
}
